"use client";

import type {
	PayoutsSessionElements,
	WhopElement,
} from "@whop/embedded-components-vanilla-js/types";
import React, { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useElementSnapshot } from "../lib/use-element-snapshot";
import { usePayoutsSession } from "./session";

/**
 * Valid element type strings that can be passed to createElement.
 */
type ElementType = keyof PayoutsSessionElements;

/**
 * Extract the options type for a given element type using indexed access.
 * PayoutsSessionElements maps element types to [Options, Element] tuples.
 */
type ElementOptionsFor<T extends ElementType> = PayoutsSessionElements[T][0];

/**
 * Base element type that all created elements conform to.
 * Using WhopElement<any, any, any> gives us access to common methods
 * (mount, unmount, on, off, updateOptions) without union type issues.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BaseElement = WhopElement<any, any, any>;

/**
 * Component definition with a constrained element type.
 * The type must be a valid element type from the SDK.
 */
interface ElementComponentDefinition<T extends ElementType = ElementType> {
	displayName: string;
	type: T;
}

interface WithFallbackPropsBase {
	className?: string;
	style?: React.CSSProperties;
	onReady?: () => void;
	fallback?: React.ReactNode;
}

export interface WithFallbackPropsRequired<
	TOptions,
> extends WithFallbackPropsBase {
	options: TOptions;
}

export interface WithFallbackPropsOptional<
	TOptions,
> extends WithFallbackPropsBase {
	options?: TOptions;
}

type WithFallbackComponent<TOptions, TRequired extends boolean> = {
	(
		props: TRequired extends true
			? WithFallbackPropsRequired<TOptions>
			: WithFallbackPropsOptional<TOptions>,
	): React.ReactNode;
	displayName: string;
	type: string;
};

/**
 * Creates a React component that wraps an embedded element with fallback support.
 *
 * @example
 * ```tsx
 * // Type-safe: options type is inferred from the element type
 * export const BalanceElement = withFallback({
 *   displayName: "BalanceElement",
 *   type: "balance-element",
 * } as const);
 *
 * // Usage - TypeScript knows the correct options type
 * <BalanceElement options={{ hidePendingBalance: true }} />
 * ```
 */
export function withFallback<
	const T extends ElementType,
	TRequired extends boolean = false,
>(
	Component: ElementComponentDefinition<T>,
): WithFallbackComponent<ElementOptionsFor<T>, TRequired> {
	type TOptions = ElementOptionsFor<T>;

	function WrappedElement({
		options,
		className,
		style,
		onReady,
		fallback,
	}: WithFallbackPropsOptional<TOptions>) {
		const payoutsSession = usePayoutsSession();
		const ref = useRef<HTMLDivElement>(null);

		const element = useMemo((): BaseElement | null => {
			if (!payoutsSession) return null;
			// Safe: T is constrained to valid element types, and options matches T
			return payoutsSession.createElement(Component, options ?? {});
		}, [payoutsSession]);

		const elementSnapshot = useElementSnapshot(element);

		const isReady = elementSnapshot?.state === "ready";

		useEffect(() => {
			if (!element) return;
			element.updateOptions(options ?? {});
		}, [options, element]);

		useEffect(() => {
			if (!element || !onReady) return;
			element.on("ready", onReady);
			return () => {
				element.off("ready", onReady);
			};
		}, [element, onReady]);

		useLayoutEffect(() => {
			if (!element || !ref.current) return;
			element.mount(ref.current);
			return () => {
				element.unmount();
			};
		}, [element, ref.current]);

		return (
			<>
				<div
					style={{ ...style, visibility: isReady ? undefined : "hidden" }}
					className={className}
					ref={ref}
				/>
				{!isReady && (fallback ?? null)}
			</>
		);
	}

	WrappedElement.displayName = Component.displayName;
	WrappedElement.type = Component.type;

	return WrappedElement as WithFallbackComponent<TOptions, TRequired>;
}
